import * as THREE from 'three';
import type { HitResult, HitTest } from './ballistics';
import type { ExplosiveSpec } from '../game/weapons';
import { preloadGLB, cloneGLBIfReady } from './gltf';
import { buildableScale } from './resizers';

const C4_FILE = 'c4.glb'; // owner-modelled brick for the thrown sticky (2026-07-11)
preloadGLB('/models/' + C4_FILE);
const SATCHEL_FILE = 'satchel.glb'; // owner-modelled satchel (2026-07-11): the budget, unreliable sticky
preloadGLB('/models/' + SATCHEL_FILE);
const GRENADE_FILE = 'grenade.glb';
preloadGLB('/models/' + GRENADE_FILE);

// PHYSICAL PROJECTILES: arrows, thrown spears, grenades, rockets. Unlike bullets (the pooled ballistics tracers),
// these are slow enough to SEE -- each carries a small mesh that flies, arcs under gravity, and on impact (or a
// grenade's fuse) fires onImpact() so the caller applies a direct hit (arrow/spear) or an explosion (grenade/rocket).
// Counts are low (you throw a few), so a plain growing pool of meshes is fine -- no instancing needed.

// sticky = thrown C4: freezes where it hits, then the fuse detonates it.
// satchel = same landing behaviour with the satchel mesh + the owner's unreliability rules (random fuse
// assigned by the thrower; ~1 in 6 lands as a DUD that never detonates and is recoverable like an arrow).
export type ProjKind = 'arrow' | 'spear' | 'grenade' | 'rocket' | 'sticky' | 'satchel';

export interface ProjOpts {
  kind: ProjKind;
  pos: THREE.Vector3;
  vel: THREE.Vector3;     // initial velocity (direction * launch speed)
  damage: number;         // direct-hit damage (arrow/spear); 0 for pure explosives
  gravityScale: number;   // multiplies base gravity (arrows/spears droop more; rockets fly flat)
  owner?: unknown;        // shooter (so the hit test can skip self)
  fuse?: number;          // grenade: detonate after this many seconds in flight even without impact
  explosive?: ExplosiveSpec | null;
  recoverId?: string;     // arrow/spear: the item id to leave STUCK + recoverable where it lands
}

interface Proj {
  alive: boolean; kind: ProjKind; mesh: THREE.Mesh; pos: THREE.Vector3; vel: THREE.Vector3;
  age: number; damage: number; gravityScale: number; owner: unknown; fuse: number; explosive: ExplosiveSpec | null;
  smokeT: number; // seconds since the last smoke puff (rockets)
  recoverId: string | null;
  stuck: boolean;              // sticky (C4): landed and frozen in place, fuse still burning
  stickDir: THREE.Vector3;     // flight direction at the moment it stuck (resolves the host wall on detonation)
}

const GRAVITY = 9.8, LIFETIME = 8, STEPMAX = 1.5;
const FWD = new THREE.Vector3(0, 0, -1); // models are authored pointing -Z
const UP = new THREE.Vector3(0, 1, 0);   // stuck charges align their top to the struck surface's normal

export class Projectiles {
  readonly group = new THREE.Group();
  private readonly pool: Proj[] = [];

  // Called once when a projectile dies: by impact (entity = what it hit, or null for static cover) or by fuse
  // (entity null, point = its position). The caller applies damage or detonates the explosive. dir is the
  // normalized flight direction at a CONTACT impact (so a rocket's direct-hit wall can be resolved) and null
  // on a fuse detonation (an airburst touches nothing). dir is a reused scratch vector -- read, don't keep.
  onImpact: ((kind: ProjKind, point: THREE.Vector3, entity: unknown, damage: number, explosive: ExplosiveSpec | null, owner: unknown, dir: THREE.Vector3 | null) => void) | null = null;

  // OWNER (2026-07-11): a spear/arrow that hits a PLAYER sticks IN them and rides along (retrievable by
  // walking up, even while it's planted in someone). main.ts resolves a hit entity to that player's body
  // Object3D; null (or no resolver) = stick in the world where it landed, like before.
  resolveAnchor: ((entity: unknown) => THREE.Object3D | null) | null = null;

  private readonly p0 = new THREE.Vector3();
  private readonly p1 = new THREE.Vector3();
  private readonly hit: HitResult = { point: new THREE.Vector3(), normal: new THREE.Vector3(), entity: null };
  private readonly tmpDir = new THREE.Vector3();
  private readonly tmpQuat = new THREE.Quaternion();

  // Rocket smoke trail: a pool of gray puffs that grow + fade behind the rocket.
  private readonly smoke: Array<{ spr: THREE.Sprite; age: number; life: number }> = [];
  private smokeMat: THREE.SpriteMaterial | null = null;

  // Arrows/spears that landed: their mesh stays embedded in the surface + is recoverable (walk near to
  // grab). `follow` set = riding a player's body (recoverNear reads its live world position instead).
  private readonly stuck: Array<{ mesh: THREE.Mesh; x: number; y: number; z: number; id: string; follow: THREE.Object3D | null }> = [];
  private readonly stickQuat = new THREE.Quaternion();
  private readonly followPos = new THREE.Vector3();

  spawn(opts: ProjOpts): void {
    let p = this.pool.find((q) => !q.alive);
    if (!p) { p = { alive: false, kind: opts.kind, mesh: buildProjMesh(opts.kind), pos: new THREE.Vector3(), vel: new THREE.Vector3(), age: 0, damage: 0, gravityScale: 1, owner: null, fuse: 0, explosive: null, smokeT: 0, recoverId: null, stuck: false, stickDir: new THREE.Vector3() }; this.pool.push(p); this.group.add(p.mesh); }
    else if (p.kind !== opts.kind) { this.group.remove(p.mesh); disposeMesh(p.mesh); p.mesh = buildProjMesh(opts.kind); this.group.add(p.mesh); p.kind = opts.kind; }
    p.alive = true; p.age = 0; p.pos.copy(opts.pos); p.vel.copy(opts.vel); p.smokeT = 0;
    p.damage = opts.damage; p.gravityScale = opts.gravityScale; p.owner = opts.owner ?? null;
    p.fuse = opts.fuse ?? Infinity; p.explosive = opts.explosive ?? null; p.recoverId = opts.recoverId ?? null;
    p.stuck = false; p.mesh.scale.setScalar(1);
    p.mesh.visible = true; p.mesh.position.copy(p.pos);
  }

  // Drop a smoke puff at (x,y,z), reusing an idle sprite or making a new one.
  private puff(x: number, y: number, z: number): void {
    if (!this.smokeMat) this.smokeMat = makeSmokeMaterial();
    let s = this.smoke.find((q) => !q.spr.visible);
    if (!s) { const spr = new THREE.Sprite(this.smokeMat.clone()); spr.renderOrder = 3; this.group.add(spr); s = { spr, age: 0, life: 0.7 }; this.smoke.push(s); }
    s.age = 0; s.life = 0.7 + (this.smoke.length % 3) * 0.1; s.spr.visible = true;
    s.spr.position.set(x, y, z); s.spr.scale.setScalar(0.25); (s.spr.material as THREE.SpriteMaterial).opacity = 0.55;
  }

  private tickSmoke(dt: number): void {
    for (const s of this.smoke) {
      if (!s.spr.visible) continue;
      s.age += dt; const t = s.age / s.life;
      if (t >= 1) { s.spr.visible = false; continue; }
      s.spr.scale.setScalar(0.25 + t * 1.1); (s.spr.material as THREE.SpriteMaterial).opacity = 0.55 * (1 - t);
    }
  }

  update(dt: number, hitTest: HitTest): void {
    for (const p of this.pool) {
      if (!p.alive) continue;
      p.age += dt;
      // Fuse (grenade airburst / stuck C4) or lifetime expiry -> detonate / die at the current position.
      // A STUCK sticky never expires by lifetime -- only its fuse fires it.
      if (p.age >= (p.stuck ? p.fuse : Math.min(p.fuse, LIFETIME))) { this.die(p, p.pos, null, false); continue; }
      if (p.stuck) { // frozen in place: just blink while the fuse burns (faster as it gets close)
        const left = Math.max(0, p.fuse - p.age);
        p.mesh.scale.setScalar(1 + 0.12 * Math.max(0, Math.sin(p.age * (left < 2 ? 22 : 9))));
        continue;
      }

      const fullLen = p.vel.length() * dt;
      const steps = Math.max(1, Math.ceil(fullLen / STEPMAX));
      const sub = dt / steps;
      let died = false;
      for (let s = 0; s < steps; s++) {
        this.p0.copy(p.pos);
        p.vel.y -= GRAVITY * p.gravityScale * sub;
        p.pos.addScaledVector(p.vel, sub);
        this.p1.copy(p.pos);
        if (hitTest(this.p0, this.p1, this.hit)) {
          if (this.hit.entity === p.owner) continue; // ignore the shooter
          if (p.kind === 'arrow' || p.kind === 'spear') {
            if (this.hit.entity != null) this.die(p, this.hit.point, this.hit.entity, true); // hit a target -> apply damage
            else { p.alive = false; p.mesh.visible = false; } // hit static cover -> no damage
            if (p.recoverId) this.stick(p.kind, this.hit.point, p.vel, p.recoverId, this.hit.entity); // leave it STUCK + recoverable
          } else if (p.kind === 'sticky' || p.kind === 'satchel') {
            // C4/satchel STICK to whatever they hit (owner ask): freeze at the contact point, fuse burning.
            p.stickDir.copy(p.vel).normalize();
            p.pos.copy(this.hit.point).addScaledVector(p.stickDir, -0.05); // rest just proud of the surface
            p.vel.set(0, 0, 0);
            p.mesh.position.copy(p.pos);
            // Face the SURFACE, not the flight path (owner): the charge's top (+Y, where the blinker/knot
            // reads) aligns to the struck face's normal -- flat and facing you on a wall, face-up on the
            // ground, hanging face-down from a ceiling.
            if (this.hit.normal.lengthSq() > 1e-6) {
              this.tmpQuat.setFromUnitVectors(UP, this.tmpDir.copy(this.hit.normal).normalize());
              p.mesh.quaternion.copy(this.tmpQuat);
            }
            // Satchel dud roll (owner: "a roll of a dice if they actually explode"): ~1 in 6 fizzles on
            // landing -- leave it stuck + recoverable (walk up to grab it back), never detonating.
            if (p.kind === 'satchel' && p.recoverId && Math.random() < 0.17) {
              p.alive = false; p.mesh.visible = false;
              this.stick(p.kind, this.hit.point, p.vel.copy(p.stickDir), p.recoverId, this.hit.entity);
              died = true; break;
            }
            p.stuck = true;
            break; // NOT died: the fuse branch above detonates it
          } else {
            this.die(p, this.hit.point, this.hit.entity, true); // grenade / rocket -> detonate on contact
          }
          died = true; break;
        }
      }
      if (died) continue;
      // Orient the mesh nose-first along velocity and move it.
      p.mesh.position.copy(p.pos);
      this.tmpDir.copy(p.vel).normalize();
      if (this.tmpDir.lengthSq() > 1e-6) { this.tmpQuat.setFromUnitVectors(FWD, this.tmpDir); p.mesh.quaternion.copy(this.tmpQuat); }
      if (p.kind === 'grenade') p.mesh.rotation.z += dt * 6; // tumble
      if (p.kind === 'rocket') { p.smokeT += dt; if (p.smokeT >= 0.028) { p.smokeT = 0; this.puff(p.pos.x - this.tmpDir.x * 0.25, p.pos.y - this.tmpDir.y * 0.25, p.pos.z - this.tmpDir.z * 0.25); } } // trail behind the nose
    }
    this.tickSmoke(dt);
    this.tickFalling(dt);
  }

  private die(p: Proj, point: THREE.Vector3, entity: unknown, contact: boolean): void {
    p.alive = false;
    p.mesh.visible = false;
    // Contact deaths pass the flight direction (rocket/grenade direct-hit resolution); fuse airbursts pass
    // null -- EXCEPT a stuck sticky, whose remembered impact direction resolves the wall it is glued to.
    const dir = contact ? this.tmpDir.copy(p.vel).normalize() : (p.kind === 'sticky' && p.stuck ? this.tmpDir.copy(p.stickDir) : null);
    this.onImpact?.(p.kind, point, entity, p.damage, p.explosive, p.owner, dir);
  }

  // Leave a fresh, oriented mesh embedded where an arrow/spear landed (the pooled projectile is freed to be
  // reused). A hit PLAYER (resolveAnchor returns their body) becomes the parent, so the spear rides them.
  private stick(kind: ProjKind, point: THREE.Vector3, vel: THREE.Vector3, id: string, entity?: unknown): void {
    const m = buildProjMesh(kind);
    this.tmpDir.copy(vel).normalize();
    m.position.set(point.x - this.tmpDir.x * 0.12, point.y - this.tmpDir.y * 0.12, point.z - this.tmpDir.z * 0.12); // sink the tip a hair into the surface
    if (this.tmpDir.lengthSq() > 1e-6) { this.stickQuat.setFromUnitVectors(FWD, this.tmpDir); m.quaternion.copy(this.stickQuat); }
    this.group.add(m);
    const anchor = entity != null ? this.resolveAnchor?.(entity) ?? null : null;
    if (anchor) anchor.attach(m); // keeps the world pose, re-parents onto the body -> the spear rides the victim
    this.stuck.push({ mesh: m, x: point.x, y: point.y, z: point.z, id, follow: anchor });
    while (this.stuck.length > 48) { const old = this.stuck.shift(); if (old) { old.mesh.parent?.remove(old.mesh); disposeMesh(old.mesh); } } // cap the world litter
  }

  // A prop an arrow/spear was stuck in broke: let those projectiles FALL to the ground (owner: "no
  // gravity???") instead of hanging in mid-air. groundY resolves the terrain height at (x,z); the fall is
  // animated in update() via the `falling` list (simple gravity + a settle tilt when it lands).
  private readonly falling: Array<{ mesh: THREE.Mesh; vy: number; groundY: number }> = [];
  dropStuckNear(px: number, pz: number, range: number, groundY: (x: number, z: number) => number): void {
    const r2 = range * range;
    for (const s of this.stuck) {
      if (s.follow) continue;
      const dx = s.x - px, dz = s.z - pz;
      if (dx * dx + dz * dz > r2) continue;
      const gy = groundY(s.x, s.z) + 0.03;
      if (s.mesh.position.y > gy + 0.05 && !this.falling.some((f) => f.mesh === s.mesh)) this.falling.push({ mesh: s.mesh, vy: 0, groundY: gy });
    }
  }
  private tickFalling(dt: number): void {
    for (let i = this.falling.length - 1; i >= 0; i--) {
      const f = this.falling[i];
      f.vy -= 9.8 * dt;
      f.mesh.position.y += f.vy * dt;
      f.mesh.rotation.x += dt * 2.2; // tips over as it drops
      if (f.mesh.position.y <= f.groundY) {
        f.mesh.position.y = f.groundY;
        f.mesh.rotation.set(Math.PI / 2 - 0.12, f.mesh.rotation.y, 0.2); // lying flat-ish on the ground
        const rec = this.stuck.find((s) => s.mesh === f.mesh);
        if (rec) rec.y = f.groundY; // recover checks stay accurate
        this.falling.splice(i, 1);
      }
    }
  }

  // Recover ONE stuck arrow/spear within `range` of (px,pz): removes its mesh + returns the item id to grant
  // (or null). A body-riding spear is checked at its LIVE position -- walk up to whoever is wearing it.
  recoverNear(px: number, pz: number, range: number): string | null {
    const r2 = range * range;
    for (let i = 0; i < this.stuck.length; i++) {
      const s = this.stuck[i];
      let sx = s.x, sz = s.z;
      if (s.follow) { s.mesh.getWorldPosition(this.followPos); sx = this.followPos.x; sz = this.followPos.z; }
      const dx = sx - px, dz = sz - pz;
      if (dx * dx + dz * dz <= r2) { s.mesh.parent?.remove(s.mesh); disposeMesh(s.mesh); this.stuck.splice(i, 1); return s.id; }
    }
    return null;
  }
}

function buildProjMesh(kind: ProjKind): THREE.Mesh {
  const lam = (c: number) => new THREE.MeshLambertMaterial({ color: c, flatShading: true });
  const g = new THREE.Group();
  if (kind === 'arrow' || kind === 'spear') {
    const len = kind === 'spear' ? 1.1 : 0.7, r = kind === 'spear' ? 0.02 : 0.012;
    const shaft = new THREE.Mesh(new THREE.CylinderGeometry(r, r * 0.8, len, 6), lam(0x6b4a2b)); shaft.rotation.x = Math.PI / 2; shaft.position.z = len * 0.12; g.add(shaft);
    const head = new THREE.Mesh(new THREE.ConeGeometry(r * 3.4, len * 0.3, 6), lam(kind === 'spear' ? 0x8f9298 : 0x9aa0a6)); head.rotation.x = -Math.PI / 2; head.position.z = -len * 0.5; g.add(head);
    const bind = new THREE.Mesh(new THREE.CylinderGeometry(r * 1.6, r * 1.6, len * 0.05, 6), lam(0x2a2620)); bind.rotation.x = Math.PI / 2; bind.position.z = -len * 0.34; g.add(bind); // binding behind the head
    if (kind === 'arrow') { const fl = new THREE.Mesh(new THREE.BoxGeometry(r * 5, 0.001, len * 0.18), lam(0xb23a3a)); fl.position.z = len * 0.42; g.add(fl); } // fletching (arrow only)
  } else if (kind === 'grenade') {
    const glb = cloneGLBIfReady('/models/' + GRENADE_FILE);
    if (glb) { glb.scale.setScalar(buildableScale(GRENADE_FILE, 0.22)); g.add(glb); }
    else g.add(new THREE.Mesh(new THREE.IcosahedronGeometry(0.085, 0), lam(0x46503a)));
  } else if (kind === 'sticky') { // C4 brick: the owner GLB when warm, else tan block + straps + a red blinker
    const glb = cloneGLBIfReady('/models/' + C4_FILE);
    if (glb) {
      glb.scale.setScalar(buildableScale(C4_FILE, 0.3));
      g.add(glb);
    } else {
      g.add(new THREE.Mesh(new THREE.BoxGeometry(0.2, 0.11, 0.11), lam(0xc9b79a)));
      for (const o of [-0.055, 0.055]) { const strap = new THREE.Mesh(new THREE.BoxGeometry(0.045, 0.125, 0.125), lam(0x2a2620)); strap.position.x = o; g.add(strap); }
      const led = new THREE.Mesh(new THREE.BoxGeometry(0.035, 0.035, 0.02), new THREE.MeshBasicMaterial({ color: 0xff3a2a })); led.position.set(0, 0.045, 0.066); g.add(led);
    }
  } else if (kind === 'satchel') { // satchel: the owner GLB when warm, else a lumpy burlap bag + rope knot
    const glb = cloneGLBIfReady('/models/' + SATCHEL_FILE);
    if (glb) {
      glb.scale.setScalar(buildableScale(SATCHEL_FILE, 0.3));
      g.add(glb);
    } else {
      g.add(new THREE.Mesh(new THREE.SphereGeometry(0.12, 8, 6).scale(1.2, 0.75, 1), lam(0x8a6a3a)));
      const knot = new THREE.Mesh(new THREE.CylinderGeometry(0.03, 0.045, 0.06, 6), lam(0x5a4426)); knot.position.y = 0.1; g.add(knot);
    }
  } else { // rocket
    const body = new THREE.Mesh(new THREE.CylinderGeometry(0.05, 0.05, 0.4, 8), lam(0x6f7378)); body.rotation.x = Math.PI / 2; g.add(body);
    const nose = new THREE.Mesh(new THREE.ConeGeometry(0.055, 0.18, 8), lam(0xc24a3a)); nose.rotation.x = -Math.PI / 2; nose.position.z = -0.29; g.add(nose);
    for (const a of [0, 1.57, 3.14, 4.71]) { const fin = new THREE.Mesh(new THREE.BoxGeometry(0.016, 0.13, 0.12), lam(0x4a4d52)); fin.position.set(Math.cos(a) * 0.07, Math.sin(a) * 0.07, 0.2); fin.rotation.z = a; g.add(fin); } // thicker swept fins
    const glow = new THREE.Mesh(new THREE.ConeGeometry(0.05, 0.24, 8), new THREE.MeshBasicMaterial({ color: 0xffa53a, transparent: true, opacity: 0.9, blending: THREE.AdditiveBlending, depthWrite: false })); glow.rotation.x = Math.PI / 2; glow.position.z = 0.33; g.add(glow); // engine flame at the tail
  }
  // Wrap the group in a single mesh-like holder: return a Mesh with the group as a child won't work; instead use a
  // Mesh whose geometry is empty and attach the group. Simpler: return the group cast as a Mesh-compatible Object3D.
  const holder = new THREE.Mesh(new THREE.BufferGeometry(), new THREE.MeshBasicMaterial({ visible: false }));
  holder.add(g);
  return holder;
}

// A soft round gray puff for the rocket smoke trail (normal blending so it reads as smoke, not fire).
function makeSmokeMaterial(): THREE.SpriteMaterial {
  const size = 64, cv = document.createElement('canvas'); cv.width = cv.height = size;
  const ctx = cv.getContext('2d')!;
  const g = ctx.createRadialGradient(size / 2, size / 2, 0, size / 2, size / 2, size / 2);
  g.addColorStop(0, 'rgba(150,150,150,0.9)'); g.addColorStop(0.5, 'rgba(120,120,120,0.5)'); g.addColorStop(1, 'rgba(110,110,110,0)');
  ctx.fillStyle = g; ctx.fillRect(0, 0, size, size);
  const tex = new THREE.CanvasTexture(cv); tex.colorSpace = THREE.SRGBColorSpace;
  return new THREE.SpriteMaterial({ map: tex, transparent: true, opacity: 0.55, depthWrite: false });
}

function disposeMesh(m: THREE.Mesh): void {
  m.traverse((o) => { const e = o as THREE.Mesh; if (e.isMesh) { e.geometry.dispose(); const mat = e.material; if (Array.isArray(mat)) mat.forEach((x) => x.dispose()); else mat.dispose(); } });
}
